import { Svg } from './Svg';
import { FacebookLink } from './FacebookLink';
import { EmailLink } from './EmailLink';
import { ClickToCopy } from './ClickToCopy';
import { MobileShareTray } from './MobileShareTray';
import { NextdoorLink } from './NextdoorLink';

interface CommonShareBarProps {
    urlToShare?:string,
    customHeader?:string,
    customBody?:string,
    isTeacher?:boolean,
    projectSummary?:string,
    essentialsListShare?: boolean,
}
type EmailShareParams = {hideEmailShare:true, tafURL?:never} | {hideEmailShare?:false, tafURL:string}
type NextdoorShareParams = {hideNextdoorShare:true, nextdoorShareBody?:never, schoolName?:never}
        | {hideNextdoorShare:false, nextdoorShareBody?:never, schoolName:string}
        | {hideNextdoorShare:false, nextdoorShareBody:string, schoolName?:never}
type OldStyleShareBarProps = CommonShareBarProps & EmailShareParams & NextdoorShareParams;

/**
 * This is the old style share bar formerly powered by sharebar.tag and sharebar.js. It is written to reuse the
 * same styles of the existing code and is not intended to be used in new code.
 * Visually it is divided into 4 sections:
 * 1. header: displays a `.donor-share-image` along with a customizable header and body message
 * 2. options: the actual share buttons
 *  a. facebook: Always
 *  b. email: hidden by hideEmailShare. If hideEmailShare is false, tafURL is required
 *  c. nextdoor: hidden by hideNextdoorShare. If hideNextdoorShare is false, either nextdoorShareBody or schoolName is required
 * 3. copy link: a click to copy link
 * 4. mobile share tray if viewed on mobile
 *
 * @param hideEmailShare hides the email share button, defaults to false
 * @param hideNextdoorShare hides the nextdoor share button, defaults to false
 * @param projectSummary optional when hideNextdoorShare is false
 * @param schoolName required when hideNextdoorShare is false
 * @param isTeacher optionally used by nextdoor sharecopy, defaults to false
 * @param nextdoorShareBody optional body text for nextdoor share instead of the standard project based template
 * @param tafURL required when hideEmailShare is false
 */
export const OldStyleShareBar = ({
    urlToShare = document.location.href,
    customHeader = 'Give this project a boost!',
    customBody = 'A chain reaction of support starts with one share.',
    hideEmailShare = false,
    hideNextdoorShare = false,
    projectSummary,
    isTeacher = false,
    essentialsListShare = false,
    nextdoorShareBody,
    schoolName,
    tafURL,
}:OldStyleShareBarProps) => {
    if (!hideEmailShare && !tafURL) {
        console.error('tafURL is required when email share is not hidden');
        return;
    }
    if (!hideNextdoorShare && !(nextdoorShareBody || schoolName)) {
        console.error('Either nextdoorShareBody or schoolName is required when nextdoor share is not hidden');
        return;
    }

    return <div className="donor-share-tools">
        <div className="header">
            <div className="donor-share-image"></div>
            <div className="message">
                <h3>{customHeader}</h3>
                <span>{customBody}</span>
            </div>
        </div>

        <div className="options">
            <div className="copy-link-mobile">
                <ClickToCopy
                    textToCopy={urlToShare}
                    getTrackingLink={window.getTrackingLink}
                    essentialsListShare={essentialsListShare}
                    pageName={pageName}>
                    <span><Svg icon="ss-link" iconFamily="social-circle"/> Copy link</span>
                </ClickToCopy>
            </div>
            <FacebookLink
                className='button-secondary small'
                urlToShare={urlToShare}
                getTrackingLink={window.getTrackingLink}
                facebookAppId={facebookAppId}
                essentialsListShare={essentialsListShare}
                pageName={pageName}
            >
                <Svg icon="ss-facebook" iconFamily="social-circle"/> Facebook
            </FacebookLink>
            {!hideEmailShare && <EmailLink
                className='button-secondary small'
                secure={false}
                tafURL={tafURL!}
                getTafEmail={window.getTafEmail}
                pageName={pageName}
                essentialsListShare={essentialsListShare}
                onEmailClick={() => {}}
            >
                <span>Email</span>
            </EmailLink>}
            {!hideNextdoorShare && <NextdoorLink
                className={'button-secondary small'}
                urlToShare={urlToShare}
                pageName={pageName}
                getTrackingLink={window.getTrackingLink}
                essentialsListShare={essentialsListShare}
                onNextdoorClick={(event, url:string) => {
                    event.preventDefault();
                    event.stopPropagation();
                    window.open(url, '_blank');
                }}
                schoolName={schoolName!}
                isTeacher={isTeacher}
                projectSummary={projectSummary}
                nextdoorShareBody={nextdoorShareBody}
            >
                <span className="icon-nextdoor"></span> Nextdoor
            </NextdoorLink>}
        </div>
        <div className="desktop">
            <ClickToCopy
                textToCopy={urlToShare}
                getTrackingLink={window.getTrackingLink}
                essentialsListShare={essentialsListShare}
                pageName={pageName}>
                <>Copy link</>
            </ClickToCopy>
        </div>
        <div className='mobile-share mobile'>
            <MobileShareTray
                className='button-secondary small heap-share-tray-share'
                urlToShare={urlToShare}
                essentialsListShare={essentialsListShare}
                getTrackingLink={window.getTrackingLink}
                projectSummary={'A chain reaction of support starts with one share.'}
                pageName={pageName}
            ><>Share on other platforms</></MobileShareTray>
        </div>
    </div>;
};
